/**
 * File item content helpers
 *
 * Normalizes stored and retrieved file chunk content before prompt or UI use.
 *
 * Primary responsibilities:
 * - Avoid JavaScript object coercion such as `[object Object]`
 * - Preserve useful text from structured parser payloads
 * - Provide a safe fallback for unreadable chunks
 *
 * Dependencies:
 * - None
 */

type ContentRecord = Record<string, unknown>

const preferredTextKeys = [
  'content',
  'text',
  'pageContent',
  'markdown',
  'body',
  'description',
  'summary'
]

function isRecord(value: unknown): value is ContentRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function normalizeString(value: string) {
  const trimmed = value.trim()
  return trimmed === '[object Object]' ? '' : value
}

export function normalizeFileItemContent(value: unknown): string {
  if (typeof value === 'string') {
    return normalizeString(value)
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  if (Array.isArray(value)) {
    return value
      .map(normalizeFileItemContent)
      .map((item) => item.trim())
      .filter(Boolean)
      .join('\n\n')
  }

  if (isRecord(value)) {
    for (const key of preferredTextKeys) {
      const normalized = normalizeFileItemContent(value[key]).trim()
      if (normalized) {
        return normalized
      }
    }

    try {
      return JSON.stringify(value, null, 2)
    } catch {
      return ''
    }
  }

  return ''
}

export function getReadableFileItemContent(value: unknown) {
  return (
    normalizeFileItemContent(value).trim() ||
    '[No readable text content was extracted from this source.]'
  )
}
